import { useEffect, useState } from 'react';
import { CreditCard, Crown, Users, FileText, ArrowRight, Receipt, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { formatDate } from '@/lib/dateFormat';

interface Usage {
  planName: string;
  planPrice: number;
  currentUsers: number;
  maxUsers: number;
  currentLeadsThisMonth: number;
  maxLeads: number;
  periodEnd: string | null;
  status: string;
  // Super-admin enables this per-vendor from the Prospects pipeline. Only then
  // does the self-serve "Upgrade Plan" CTA show for this tenant.
  upgradeCtaEnabled?: boolean;
}

interface Subscription {
  id: string;
  status: string;
  currentPeriodEnd: string;
  plan: { name: string; price: number; features: string[] };
}

interface Invoice {
  id: string;
  provider: string;
  providerRef: string | null;
  planName: string | null;
  amountCents: number;
  currency: string;
  status: string;
  createdAt: string;
}

// Format minor-unit amount into a currency string (₹ for INR, $ for USD).
function formatAmount(amountCents: number, currency: string): string {
  const amt = (amountCents || 0) / 100;
  const c = (currency || 'usd').toLowerCase();
  const frac = amt % 1 ? 2 : 0;
  if (c === 'inr') return `₹${amt.toLocaleString('en-IN', { minimumFractionDigits: frac, maximumFractionDigits: 2 })}`;
  if (c === 'usd') return `$${amt.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: 2 })}`;
  return `${amt.toLocaleString(undefined, { minimumFractionDigits: frac, maximumFractionDigits: 2 })} ${c.toUpperCase()}`;
}

const PROVIDER_LABEL: Record<string, string> = { stripe: 'Card (Stripe)', razorpay: 'Razorpay' };

function barColor(current: number, max: number): string {
  if (max <= 0) return 'bg-emerald-500';
  const pct = (current / max) * 100;
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 70) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function barWidth(current: number, max: number): string {
  if (max <= 0) return '10%';
  return `${Math.min((current / max) * 100, 100)}%`;
}

export function BillingPage() {
  const [usage, setUsage] = useState<Usage | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch in parallel and flip "loading" off only after all resolve —
    // avoids the "Loading… Free → Loading… Free Renews → Growth Plan"
    // flicker where partial state painted misleading intermediate frames.
    let alive = true;
    Promise.all([
      api.get('/subscriptions/usage').then(r => r.data).catch(() => null),
      api.get('/subscriptions/me').then(r => r.data).catch(() => null),
      api.get('/subscriptions/invoices').then(r => r.data).catch(() => []),
    ]).then(([u, s, inv]) => {
      if (!alive) return;
      setUsage(u);
      setSubscription(s);
      setInvoices(Array.isArray(inv) ? inv : []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  return (
    <DashboardLayout title="Billing">
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[#6366F1]">Billing & Plan</h2>
          <p className="text-sm text-[#64748B] mt-1">Manage your subscription and usage</p>
        </div>

        {/* Expired-plan renew banner — a paid plan is never auto-downgraded, so
            we persistently nudge the vendor to renew (or they downgrade themselves). */}
        {!loading && usage && usage.planPrice > 0 && subscription &&
          (subscription.status !== 'active' || (subscription.currentPeriodEnd && new Date(subscription.currentPeriodEnd) < new Date())) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">Your {usage.planName} plan has expired</p>
              <p className="text-sm text-amber-700 mt-0.5">Renew to keep your higher limits and premium features. Your data stays safe.</p>
            </div>
            <button
              onClick={() => navigate('/choose-plan')}
              className="flex-shrink-0 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Renew
            </button>
          </div>
        )}

        {/* Current Plan Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Crown className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                {loading ? (
                  // Skeleton — avoids the "Free → Free Renews → Growth"
                  // flicker we saw on slow networks where each fetch
                  // resolved separately.
                  <>
                    <div className="h-5 w-32 bg-slate-200 rounded animate-pulse mb-2" />
                    <div className="h-3.5 w-44 bg-slate-100 rounded animate-pulse" />
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-bold text-slate-900">{usage?.planName ?? 'Free'} Plan</h3>
                    <p className="text-sm text-slate-400">
                      {usage && usage.planPrice > 0 ? `$${usage.planPrice}/month` : 'Free'}
                      {subscription?.currentPeriodEnd && ` · Renews ${formatDate(subscription.currentPeriodEnd)}`}
                    </p>
                  </>
                )}
              </div>
            </div>
            {usage?.upgradeCtaEnabled && (
              <button
                onClick={() => navigate('/choose-plan')}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                Upgrade Plan <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Plan Features */}
          {subscription?.plan?.features && (
            <div className="flex flex-wrap gap-2 mb-6">
              {(subscription.plan.features as string[]).map((f, i) => (
                <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-medium">
                  {f}
                </span>
              ))}
            </div>
          )}

          {/* Usage Meters */}
          {usage && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#6366F1]" />
                    <span className="text-sm font-medium text-slate-700">Users</span>
                  </div>
                  <span className="text-sm font-bold text-slate-800">
                    {usage.currentUsers} / {usage.maxUsers > 0 ? usage.maxUsers : 'Unlimited'}
                  </span>
                </div>
                <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${barColor(usage.currentUsers, usage.maxUsers)}`}
                    style={{ width: barWidth(usage.currentUsers, usage.maxUsers) }}
                  />
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm font-medium text-slate-700">Leads this month</span>
                  </div>
                  <span className="text-sm font-bold text-slate-800">
                    {usage.currentLeadsThisMonth} / {usage.maxLeads > 0 ? usage.maxLeads : 'Unlimited'}
                  </span>
                </div>
                <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${barColor(usage.currentLeadsThisMonth, usage.maxLeads)}`}
                    style={{ width: barWidth(usage.currentLeadsThisMonth, usage.maxLeads) }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Billing Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="h-5 w-5 text-slate-600" />
            <h3 className="text-base font-semibold text-slate-900">Billing Information</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-50">
              <span className="text-slate-500">Status</span>
              <span className={`font-medium ${loading ? 'text-slate-400' : subscription?.status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>
                {loading ? '—' : subscription?.status === 'active' ? 'Active' : subscription?.status || 'No subscription'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-50">
              <span className="text-slate-500">Plan</span>
              <span className="font-medium text-slate-800">{loading ? '—' : usage?.planName || '—'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-50">
              <span className="text-slate-500">Monthly Price</span>
              <span className="font-medium text-slate-800">{loading ? '—' : usage?.planPrice ? `$${usage.planPrice}` : 'Free'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Next Billing</span>
              <span className="font-medium text-slate-800">
                {loading ? '—' : subscription?.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Billing History */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <Receipt className="h-5 w-5 text-slate-600" />
            <h3 className="text-base font-semibold text-slate-900">Billing History</h3>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[0, 1].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}
            </div>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No invoices yet. Your paid invoices will appear here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-100">
                    <th className="py-2.5 pr-4 whitespace-nowrap">Date</th>
                    <th className="py-2.5 pr-4 whitespace-nowrap">Plan</th>
                    <th className="py-2.5 pr-4 whitespace-nowrap">Method</th>
                    <th className="py-2.5 pr-4 whitespace-nowrap text-right">Amount</th>
                    <th className="py-2.5 whitespace-nowrap text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-slate-50/60">
                      <td className="py-3 pr-4 text-slate-700 whitespace-nowrap">{formatDate(inv.createdAt)}</td>
                      <td className="py-3 pr-4 text-slate-700 whitespace-nowrap">{inv.planName || '—'}</td>
                      <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">{PROVIDER_LABEL[inv.provider] || inv.provider}</td>
                      <td className="py-3 pr-4 font-semibold text-slate-800 whitespace-nowrap text-right">{formatAmount(inv.amountCents, inv.currency)}</td>
                      <td className="py-3 whitespace-nowrap text-right">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                          inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700'
                            : inv.status === 'failed' ? 'bg-red-50 text-red-600'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
